import { Search } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { useOverlay } from '../../../contexts/OverlayContext';
import { useScreenLogging } from '../../../hooks/useScreenLogging';
import { typography } from '../../../styles/typography';
import { getAllSites } from '../../SiteManagement/siteApi';
import Card from '../../ui/card';
import { fetchDishDataset } from '../RestorantSites/RestorantSiteApi';
import DishAnalyticsChart from './DishAnalyticsChart';

const formatDateForAPI = (date) => date.toISOString().split('T')[0];

// /** Dummy sites when API returns none */
// const DUMMY_SITES = [
//   { site_id: 'dummy-site-1', site_name: 'Main Restaurant' },
//   { site_id: 'dummy-site-2', site_name: 'Cafe Downtown' },
// ];

// /** Dummy dish dataset for display when API is unavailable or returns empty */
// const getDummyDishData = () => [
//   {
//     dish_id: 'dish-001',
//     order_event_timestamp: '2025-01-15T12:30:00',
//     qty: 2,
//     current_status: 'delivered',
//     server_user_name: 'John Doe',
//     stages: {
//       prep: { start_timestamp: '2025-01-15T12:30:00', end_timestamp: '2025-01-15T12:45:00' },
//       cook: { start_timestamp: '2025-01-15T12:45:00', end_timestamp: '2025-01-15T13:05:00' },
//     },
//     dish_info: { name: 'Margherita Pizza', type: 'main', cuisine: 'Italian', Price: 12.99 },
//   },
//   {
//     dish_id: 'dish-001',
//     order_event_timestamp: '2025-01-16T13:00:00',
//     qty: 1,
//     current_status: 'delivered',
//     server_user_name: 'Jane Smith',
//     stages: {
//       prep: { start_timestamp: '2025-01-16T13:00:00', end_timestamp: '2025-01-16T13:12:00' },
//       cook: { start_timestamp: '2025-01-16T13:12:00', end_timestamp: '2025-01-16T13:25:00' },
//     },
//     dish_info: { name: 'Margherita Pizza', type: 'main', cuisine: 'Italian', Price: 12.99 },
//   },
//   {
//     dish_id: 'dish-002',
//     order_event_timestamp: '2025-01-15T12:45:00',
//     qty: 1,
//     current_status: 'delivered',
//     server_user_name: 'John Doe',
//     stages: { toss: { start_timestamp: '2025-01-15T12:45:00', end_timestamp: '2025-01-15T12:52:00' } },
//     dish_info: { name: 'Caesar Salad', type: 'starter', cuisine: 'American', Price: 8.5 },
//   },
//   {
//     dish_id: 'dish-003',
//     order_event_timestamp: '2025-01-15T13:15:00',
//     qty: 1,
//     current_status: 'cooking',
//     server_user_name: 'Jane Smith',
//     stages: { grill: { start_timestamp: '2025-01-15T13:15:00', end_timestamp: '2025-01-15T13:38:00' } },
//     dish_info: { name: 'Grilled Chicken', type: 'main', cuisine: 'American', Price: 15.99 },
//   },
//   {
//     dish_id: 'dish-003',
//     order_event_timestamp: '2025-01-16T14:00:00',
//     qty: 2,
//     current_status: 'preparing',
//     server_user_name: 'Mike Wilson',
//     stages: { grill: { start_timestamp: '2025-01-16T14:00:00', end_timestamp: '2025-01-16T14:28:00' } },
//     dish_info: { name: 'Grilled Chicken', type: 'main', cuisine: 'American', Price: 15.99 },
//   },
//   {
//     dish_id: 'dish-004',
//     order_event_timestamp: '2025-01-15T13:20:00',
//     qty: 2,
//     current_status: 'pending',
//     dish_info: { name: 'Mango Smoothie', type: 'beverage', cuisine: 'Beverages', Price: 6.5 },
//   },
//   {
//     dish_id: 'dish-005',
//     order_event_timestamp: '2025-01-15T14:00:00',
//     qty: 1,
//     current_status: 'served',
//     dish_info: { name: 'Chocolate Brownie', type: 'dessert', cuisine: 'Desserts', Price: 7.99 },
//   },
//   {
//     dish_id: 'dish-006',
//     order_event_timestamp: '2025-01-15T11:45:00',
//     qty: 3,
//     current_status: 'preparing',
//     dish_info: { name: 'Pepperoni Pizza', type: 'main', cuisine: 'Italian', Price: 14.99 },
//   },
//   {
//     dish_id: 'dish-007',
//     order_event_timestamp: '2025-01-16T12:00:00',
//     qty: 1,
//     current_status: 'cancelled',
//     dish_info: { name: 'Fish & Chips', type: 'main', cuisine: 'British', Price: 13.5 },
//   },
//   {
//     dish_id: 'dish-008',
//     order_event_timestamp: '2025-01-15T19:00:00',
//     qty: 2,
//     current_status: 'delivered',
//     dish_info: { name: 'Butter Chicken', type: 'main', cuisine: 'Indian', Price: 16.99 },
//   },
//   {
//     dish_id: 'dish-008',
//     order_event_timestamp: '2025-01-16T19:30:00',
//     qty: 1,
//     current_status: 'delivered',
//     dish_info: { name: 'Butter Chicken', type: 'main', cuisine: 'Indian', Price: 16.99 },
//   },
// ];

/** Default date range: previous month same day (clamped) to today */
const getDefaultDateRange = () => {
  const today = new Date();
  const prevMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  const lastDayPrev = new Date(
    prevMonth.getFullYear(),
    prevMonth.getMonth() + 1,
    0
  ).getDate();
  const startDay = Math.min(today.getDate(), lastDayPrev);
  const startDate = new Date(
    prevMonth.getFullYear(),
    prevMonth.getMonth(),
    startDay
  );
  return { startDate, endDate: new Date(today) };
};

const ChefAnalytics = () => {
  // Log screen view
  useScreenLogging('ChefAnalytics', {
    component: 'ChefAnalytics',
  });
  const { openOverlay } = useOverlay();
  const { startDate: defaultStart, endDate: defaultEnd } = getDefaultDateRange();

  const [siteList, setSiteList] = useState([]);
  const [selectedSiteName, setSelectedSiteName] = useState(null);
  const [selectedSiteId, setSelectedSiteId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [startDate, setStartDate] = useState(formatDateForAPI(defaultStart));
  const [endDate, setEndDate] = useState(formatDateForAPI(defaultEnd));

  const [dishList, setDishList] = useState([]);
  const [dishRowsFull, setDishRowsFull] = useState([]); // all order lines for chart
  const [filteredDishList, setFilteredDishList] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [sitesError, setSitesError] = useState(null);

  const loadSitesFromDishDataset = useCallback(async () => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    if (!startDate || !endDate || start > end) return;

    setIsLoading(true);
    setSitesError(null);
    try {
      const response = await getAllSites();
      const allSites = Array.isArray(response?.sites)
        ? response.sites
        : Array.isArray(response)
          ? response
          : [];
      const seenSiteIds = new Set();
      const list = [];

      for (const site of allSites) {
        const siteId =
          site?.site_id ?? site?.siteId ?? site?.id ?? site?._id ?? '';
        if (!siteId) continue;
        try {
          const data = await fetchDishDataset({
            siteId,
            startDate: start,
            endDate: end,
          });
          if (Array.isArray(data) && data.length > 0) {
            const first = data[0];
            const id = first?.site_id ?? siteId;
            const name = first?.site_name ?? site?.site_name ?? site?.name ?? `Site ${id}`;
            if (!seenSiteIds.has(id)) {
              seenSiteIds.add(id);
              list.push({ site_id: id, site_name: name });
            }
          }
        } catch (_) {
          // Skip site if dish dataset fails
        }
      }

      // If no sites from API, show empty list
      setSiteList(list);
    } catch (err) {
      console.error('ChefAnalytics: Error loading sites from dish dataset', err);
      setSitesError(err?.message || 'Unable to load sites.');
      setSiteList([]);
    } finally {
      setIsLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    loadSitesFromDishDataset();
  }, [loadSitesFromDishDataset]);

  // Re-fetch dishes when date range changes and a site is selected
  const loadDishesForSelectedSite = useCallback(async () => {
    if (!selectedSiteId) return;
    const start = new Date(startDate);
    const end = new Date(endDate);
    if (!startDate || !endDate || start > end) return;

    setIsLoading(true);
    try {
      const data = await fetchDishDataset({
        siteId: selectedSiteId,
        startDate: start,
        endDate: end,
      });
      const items = Array.isArray(data) ? data : [];
      setDishRowsFull(items);
      const uniqueByDishName = new Map();
      for (const dish of items) {
        const nameKey =
          (dish?.dish_info?.name ?? '')
            .toString()
            .toLowerCase()
            .trim() || '__unnamed__';
        if (!uniqueByDishName.has(nameKey)) {
          uniqueByDishName.set(nameKey, dish);
        }
      }
      const uniqueList = Array.from(uniqueByDishName.values());
      setDishList(uniqueList);
      setFilteredDishList(uniqueList);
    } catch (err) {
      console.error('ChefAnalytics: Error fetching dishes', err);
      setDishList([]);
      setDishRowsFull([]);
      setFilteredDishList([]);
    } finally {
      setIsLoading(false);
    }
  }, [startDate, endDate, selectedSiteId]);

  useEffect(() => {
    if (!selectedSiteId) return;
    loadDishesForSelectedSite();
  }, [selectedSiteId, loadDishesForSelectedSite]);

  // Clear selection if selected site is no longer in siteList (e.g. after date change)
  useEffect(() => {
    if (!selectedSiteId) return;
    const siteStillInList = siteList.some((s) => s.site_id === selectedSiteId);
    if (!siteStillInList) {
      setSelectedSiteName(null);
      setSelectedSiteId(null);
      setDishList([]);
      setDishRowsFull([]);
      setFilteredDishList([]);
    }
  }, [siteList, selectedSiteId]);

  const handleSiteChange = (e) => {
    const name = e.target.value;
    if (!name) {
      setSelectedSiteName(null);
      setSelectedSiteId(null);
      setDishList([]);
      setDishRowsFull([]);
      setFilteredDishList([]);
      return;
    }
    const site = siteList.find((s) => s.site_name === name);
    if (!site) return;

    setSelectedSiteName(site.site_name);
    setSelectedSiteId(site.site_id);
    setSearchQuery('');
    // Dish fetch is handled by useEffect when selectedSiteId changes
  };

  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredDishList(dishList);
      return;
    }
    const q = searchQuery.toLowerCase().trim();
    const filtered = dishList.filter((dish) => {
      const dishName =
        (dish?.dish_info?.name ?? '')
          .toString()
          .toLowerCase() || '';
      return dishName.includes(q);
    });
    setFilteredDishList(filtered);
  }, [searchQuery, dishList]);

  const handleDishClick = (dish) => {
    const info = dish?.dish_info ?? {};
    const dishName = info.name ?? 'Unnamed Dish';
    openOverlay('businessAnalyticsChart', {
      title: `Dish: ${dishName}`,
      content: (
        <DishAnalyticsChart
          dishName={dishName}
          allDishes={dishRowsFull}
        />
      ),
    });
  };

  return (
    <div className="w-full h-full">
      <div className="mb-6">
        <h1 className={`${typography.h2} text-black mb-1`}>
          Chef Analytics
        </h1>
        <p className={`${typography.p} text-gray-600 text-sm`}>
          View dishes by site and date range; tap a dish for analytics
        </p>
      </div>

      {sitesError && (
        <Card.Gradient className="p-4 mb-4 border border-red-200 bg-red-50">
          <p className={`${typography.p} text-red-700`}>{sitesError}</p>
        </Card.Gradient>
      )}

      <div className="flex flex-col gap-4">
        {/* Date range + Select site: From/To same line on mobile, all three on laptop */}
        <div className="grid grid-cols-2 lg:grid-cols-[1fr_1fr_1fr] gap-4 lg:items-end">
          <div>
            <label
              className={`block ${typography.h5} text-gray-800 mb-2`}
              style={{ color: 'var(--primary, #0a8089)' }}
            >
              From
            </label>
            <input
              type="date"
              value={startDate}
              max={endDate || undefined}
              onChange={(e) => {
                const val = e.target.value;
                setStartDate(val);
                if (val && endDate && val > endDate) {
                  setEndDate(val);
                }
              }}
              className="w-full h-12 px-4 bg-white rounded-lg border border-gray-300 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/40 text-sm"
            />
          </div>
          <div>
            <label
              className={`block ${typography.h5} text-gray-800 mb-2`}
              style={{ color: 'var(--primary, #0a8089)' }}
            >
              To
            </label>
            <input
              type="date"
              value={endDate}
              min={startDate || undefined}
              onChange={(e) => {
                const val = e.target.value;
                setEndDate(val);
                if (val && startDate && val < startDate) {
                  setStartDate(val);
                }
              }}
              className="w-full h-12 px-4 bg-white rounded-lg border border-gray-300 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/40 text-sm"
            />
          </div>
          <div className="col-span-2 lg:col-span-1">
            <label className={`block ${typography.h5} text-gray-800 mb-2`}>
              Select site
            </label>
            <select
              value={selectedSiteName ?? ''}
              onChange={handleSiteChange}
              disabled={isLoading}
              className="w-full h-12 px-4 bg-white rounded-lg border border-gray-300 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/40 text-sm"
            >
              <option value="">Select site</option>
              {siteList.map((site) => (
                <option key={site.site_id} value={site.site_name}>
                  {site.site_name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Dish list section */}
        {selectedSiteName && (
          <>
            <div>
              <h3 className={`${typography.h5} text-gray-800 mb-2`}>
                Dishes based on date range
              </h3>
              <div className="relative">
                <Search
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500"
                  style={{ color: 'var(--primary, #0a8089)' }}
                />
                <input
                  type="text"
                  placeholder="Search dishes..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full h-11 pl-10 pr-4 rounded-lg border border-gray-300 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/40 text-sm"
                />
              </div>
            </div>

            {isLoading ? (
              <div className="space-y-2">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Card.Gradient key={i} className="p-4">
                    <div className="h-4 bg-gray-200 rounded animate-pulse w-2/3 mb-2" />
                    <div className="h-3 bg-gray-100 rounded animate-pulse w-1/2" />
                  </Card.Gradient>
                ))}
              </div>
            ) : (
              <div className="space-y-2 max-h overflow-y-auto">
                {filteredDishList.length === 0 ? (
                  <Card.Gradient className="p-6 text-center">
                    <p className={typography.p} style={{ color: '#666' }}>
                      {searchQuery
                        ? 'No dishes match your search.'
                        : 'No dishes in this date range for the selected site.'}
                    </p>
                  </Card.Gradient>
                ) : (
                  filteredDishList.map((dish, index) => {
                    const info = dish?.dish_info ?? {};
                    const name = info.name ?? 'Unnamed Dish';
                    const type = info.type ?? '—';
                    const cuisine = info.cuisine ?? '—';
                    const price = info.Price != null ? Number(info.Price) : null;
                    return (
                      <Card.Gradient
                        key={(dish.dish_id ?? index) + '-' + name}
                        className="p-4 cursor-pointer hover:bg-gray-50/80 transition-colors border border-gray-200"
                        onClick={() => handleDishClick(dish)}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <p className={`${typography.h5} text-black truncate`}>
                              {name}
                            </p>
                            <p className={`${typography.p} text-gray-600 text-sm`}>
                              Type: {type}, Cuisine: {cuisine}
                            </p>
                          </div>
                          {price != null && (
                            <span className={`${typography.sh2} text-gray-800 whitespace-nowrap`}>
                              ₹{price.toLocaleString('en-IN')}
                            </span>
                          )}
                        </div>
                      </Card.Gradient>
                    );
                  })
                )}
              </div>
            )}
          </>
        )}

        {!isLoading && !selectedSiteName && siteList.length === 0 && !sitesError && (
          <Card.Gradient className="p-8 text-center">
            <p className={typography.p} style={{ color: '#666' }}>
              No sites with dish data in the selected date range. Try a different range.
            </p>
          </Card.Gradient>
        )}

        {!isLoading && !selectedSiteName && siteList.length > 0 && (
          <Card.Gradient className="p-6 text-center">
            <p className={typography.p} style={{ color: '#666' }}>
              Select a site to view dishes.
            </p>
          </Card.Gradient>
        )}
      </div>
    </div>
  );
};

export default ChefAnalytics;
